import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from 'react';
import AdminLayout from '../../Components/Admin/AdminLayout';
import { Table, Button, Spinner, Collapse, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Container } from 'reactstrap';
import { useSelector } from 'react-redux';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
export default function UsersAdmin() {
  const {
    t
  } = useTranslation();
  const [users, setUsers] = useState([]);
  const currentUserEmail = useSelector(state => state.auth.currentUser?.email);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [assoc, setAssoc] = useState({});
  const [query, setQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    username: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editUser, setEditUser] = useState({
    id: '',
    email: '',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    isAdmin: false
  });

  const handleAddSubmit = async e => {
    e.preventDefault();
    setAddLoading(true);
    setAddError('');

    try {
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const res = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });
      const json = await res.json();
      if (!json || !json.success) throw new Error(json?.error?.message || 'Failed to create');
      setShowAddModal(false);
      setNewUser({
        email: '',
        username: '',
        password: '',
        firstName: '',
        lastName: ''
      });
      await load();
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEditSubmit = async e => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');

    try {
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const res = await fetch(`${API_BASE}/admin/users/${editUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editUser)
      });
      const json = await res.json();
      if (!json || !json.success) throw new Error(json?.error?.message || 'Failed to save');
      setShowEditModal(false);
      await load();
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditLoading(false);
    }
  };

  async function load(q = '') {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const res = await fetch(`${API_BASE}/admin/users?query=${q}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const json = await res.json();
      if (!json || !json.success) throw new Error(json?.error?.message || 'Failed to load');
      setUsers((json.data && json.data.users) || json.users || json.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function remove(userId) {
    if (!window.confirm('Are you sure you want to delete this user and all related data (profiles, cards, connections, messages)? This is permanent!')) return;

    try {
      setBusyId(userId);
      setError('');
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const json = await res.json();
      if (!json || !json.success) throw new Error(json?.error?.message || 'Failed to delete');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  }

  async function toggle(userId) {
    setOpen(prev => ({ ...prev,
      [userId]: !prev[userId]
    }));

    if (!assoc[userId]) {
      try {
        const token = localStorage.getItem('HMI_AUTH_TOKEN');
        const res = await fetch(`${API_BASE}/admin/users/${userId}/associations`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const json = await res.json();
        if (!json || !json.success) throw new Error(json?.error?.message || 'Failed to load associations');
        setAssoc(prev => ({ ...prev,
          [userId]: json.data
        }));
      } catch (e) {
        setError(e.message);
      }
    }
  } // Load on mount


  useEffect(() => {
    load('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Debounced search

  useEffect(() => {
    if (query === '') {
      load('');
      return;
    }

    const timer = setTimeout(() => load(query), 500);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  return <AdminLayout>
            <Container fluid className="px-4">
                <h3>{t("Admin Users")}{currentUserEmail && <span style={{
          fontSize: '18px',
          color: '#6c757d',
          marginLeft: '10px',
          verticalAlign: 'middle'
        }}>({currentUserEmail})</span>}</h3>
                <div className='form-inline mb-3 d-flex justify-content-between w-100'>
                    <div>
                        <input className='form-control mr-2' placeholder={t("Search by email, username, or id")} value={query} onChange={e => setQuery(e.target.value)} />
                        <Button color='secondary' size='sm' onClick={() => load(query)}>{t("Search")}</Button>
                    </div>
                    <Button color='primary' size='sm' onClick={() => setShowAddModal(true)}>{t("+ Create User")}</Button>
                </div>
                {loading && <p>{t("Loading\u2026")}</p>}
                {error && <p style={{
        color: '#e74c3c'
      }}>{error}</p>}
                {!loading && !error && <>
                        <p className="mb-2">{t("Found")}{Array.isArray(users) ? users.length : 0}{t("users")}</p>
                        {!Array.isArray(users) || users.length === 0 ? <p className="text-muted">{t("No users found.")}</p> : <Table striped responsive size='sm' className="admin-users-table">
                                <thead>
                                    <tr>
                                        <th>{t("ID")}</th>
                                        <th>{t("Name")}</th>
                                        <th>{t("Email")}</th>
                                        <th>{t("Username")}</th>
                                        <th>{t("Admin")}</th>
                                        <th>{t("Health / Status")}</th>
                                        <th>{t("Created")}</th>
                                        <th>{t("Actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(u => <React.Fragment key={u.id}>
                                    <tr>
                                        <td>
                                            <code style={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    cursor: 'help'
                  }} title={u.id}>
                                                {u.id.slice(0, 8)}…
                                            </code>
                                        </td>
                                        <td>
                                            {`${u.firstName || ''} ${u.lastName || ''}`.trim() || <span className="text-muted" style={{
                    fontSize: '0.85rem'
                  }}>-</span>}
                                        </td>
                                        <td>{u.email}</td>
                                        <td>{u.username}</td>
                                        <td>
                                            <span className={`badge ${u.isAdmin ? 'badge-danger' : 'badge-secondary'}`} style={{
                    fontSize: '0.75rem',
                    padding: '3px 6px'
                  }}>
                                                {u.isAdmin ? 'Admin' : 'User'}
                                            </span>
                                        </td>
                                        <td>
                                            {u.hasAnomaly ? (
                                                <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                                                    <span
                                                        className="badge badge-warning text-dark"
                                                        style={{ fontSize: '0.72rem', padding: '2px 5px', fontWeight: 'bold' }}
                                                        title={(u.anomalies || []).join(', ')}
                                                    >
                                                        <span role="img" aria-label="warning">⚠️</span> Issue
                                                    </span>
                                                    {(u.anomalies || []).map((anomaly, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="badge badge-danger"
                                                            style={{ fontSize: '0.68rem', padding: '2px 4px' }}
                                                        >
                                                            {anomaly}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span
                                                    className="badge badge-success"
                                                    style={{ fontSize: '0.72rem', padding: '2px 6px' }}
                                                >
                                                    ✓ Healthy
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span title={new Date(u.createdAt).toLocaleString()} style={{
                    fontSize: '0.85rem'
                  }}>
                                                {new Date(u.createdAt).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <Button color='info' size='sm' className='btn-compact mr-2' onClick={() => {
                      setEditUser({
                        id: u.id,
                        email: u.email || '',
                        username: u.username || '',
                        firstName: u.firstName || '',
                        lastName: u.lastName || '',
                        isAdmin: !!u.isAdmin,
                        password: ''
                      });
                      setShowEditModal(true);
                    }}>{t("\u270F\uFE0F Edit")}</Button>
                                                <Button color='secondary' size='sm' className='btn-compact mr-2' onClick={() => toggle(u.id)}>
                                                    {open[u.id] ? 'Hide' : 'Show'}
                                                </Button>
                                                <Button color='danger' size='sm' className='btn-compact' disabled={busyId === u.id} onClick={() => remove(u.id)}>
                                                    {busyId === u.id ? <Spinner size='sm' style={{
                        width: '0.8rem',
                        height: '0.8rem'
                      }} /> : '🗑️ Delete'}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="collapse-row">
                                        <td colSpan={8}>
                                            <Collapse isOpen={!!open[u.id]}>
                                                <Card className='bg-dark text-white m-2'>
                                                    <CardBody className='text-white p-3'>
                                                        {assoc[u.id] ? <div className='row'>
                                                                <div className='col-md-4'>
                                                                    <h6>{t("Profile")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify({
                                id: assoc[u.id].profileId
                              }, null, 2)}</pre>
                                                                    <h6>{t("Properties")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify(assoc[u.id].properties, null, 2)}</pre>
                                                                    <h6>{t("Cards")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify(assoc[u.id].cards, null, 2)}</pre>
                                                                </div>
                                                                <div className='col-md-4'>
                                                                    <h6>{t("Connections (From)")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify(assoc[u.id].connectionsFrom, null, 2)}</pre>
                                                                    <h6>{t("Connections (To)")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify(assoc[u.id].connectionsTo, null, 2)}</pre>
                                                                </div>
                                                                <div className='col-md-4'>
                                                                    <h6>{t("Invitations")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify({
                                from: assoc[u.id].invitationsFrom,
                                to: assoc[u.id].invitationsTo
                              }, null, 2)}</pre>
                                                                    <h6>{t("Messages")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify({
                                from: assoc[u.id].messagesFrom,
                                to: assoc[u.id].messagesTo
                              }, null, 2)}</pre>
                                                                    <h6>{t("QR Codes")}</h6>
                                                                    <pre style={{
                              whiteSpace: 'pre-wrap',
                              color: '#fff'
                            }}>{JSON.stringify(assoc[u.id].qrcodes, null, 2)}</pre>
                                                                </div>
                                                            </div> : <p>{t("Loading\u2026")}</p>}
                                                    </CardBody>
                                                </Card>
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>)}
                                </tbody>
                            </Table>}
                    </>}
            </Container>
            <Modal isOpen={showAddModal} toggle={() => setShowAddModal(!showAddModal)}>
                <Form onSubmit={handleAddSubmit} autoComplete="off">
                    <ModalHeader toggle={() => setShowAddModal(!showAddModal)}>{t("Create New User")}</ModalHeader>
                    <ModalBody>
                        {addError && <p style={{
            color: '#e74c3c'
          }}>{addError}</p>}
                        <FormGroup>
                            <Label>{t("Email")}</Label>
                            <Input type="email" required value={newUser.email} onChange={e => setNewUser({ ...newUser,
              email: e.target.value
            })} style={{
              backgroundColor: '#fff',
              color: '#000'
            }} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("Username")}</Label>
                            <Input type="text" required autoComplete="off" value={newUser.username} onChange={e => setNewUser({ ...newUser,
              username: e.target.value
            })} style={{
              backgroundColor: '#fff',
              color: '#000'
            }} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("Password")}</Label>
                            <Input type="password" required autoComplete="new-password" value={newUser.password} onChange={e => setNewUser({ ...newUser,
              password: e.target.value
            })} style={{
              backgroundColor: '#fff',
              color: '#000'
            }} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("First Name (Optional)")}</Label>
                            <Input type="text" value={newUser.firstName} onChange={e => setNewUser({ ...newUser,
              firstName: e.target.value
            })} style={{
              backgroundColor: '#fff',
              color: '#000'
            }} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("Last Name (Optional)")}</Label>
                            <Input type="text" value={newUser.lastName} onChange={e => setNewUser({ ...newUser,
              lastName: e.target.value
            })} style={{
              backgroundColor: '#fff',
              color: '#000'
            }} />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowAddModal(false)}>{t("Cancel")}</Button>
                        <Button color="primary" type="submit" disabled={addLoading}>
                            {addLoading ? <Spinner size="sm" /> : 'Create'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
            <Modal isOpen={showEditModal} toggle={() => setShowEditModal(!showEditModal)}>
                <Form onSubmit={handleEditSubmit} autoComplete="off">
                    <ModalHeader toggle={() => setShowEditModal(!showEditModal)}>{t("Edit User")}</ModalHeader>
                    <ModalBody>
                        {editError && <p style={{
            color: '#e74c3c'
          }}>{editError}</p>}
                        <FormGroup>
                            <Label>{t("Email")}</Label>
                            <Input type="email" required value={editUser.email} onChange={e => setEditUser({ ...editUser,
              email: e.target.value
            })} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("Username")}</Label>
                            <Input type="text" required autoComplete="off" value={editUser.username} onChange={e => setEditUser({ ...editUser,
              username: e.target.value
            })} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("New Password (Optional)")}</Label>
                            <Input type="password" autoComplete="new-password" placeholder={t("Leave blank to keep current password")} value={editUser.password} onChange={e => setEditUser({ ...editUser,
              password: e.target.value
            })} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("First Name (Optional)")}</Label>
                            <Input type="text" value={editUser.firstName} onChange={e => setEditUser({ ...editUser,
              firstName: e.target.value
            })} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t("Last Name (Optional)")}</Label>
                            <Input type="text" value={editUser.lastName} onChange={e => setEditUser({ ...editUser,
              lastName: e.target.value
            })} />
                        </FormGroup>
                        <FormGroup check className="mt-3 mb-2">
                            <Label check>
                                <Input type="checkbox" checked={editUser.isAdmin} onChange={e => setEditUser({ ...editUser,
                isAdmin: e.target.checked
              })} />{' '}{t("Administrator Access")}</Label>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowEditModal(false)}>{t("Cancel")}</Button>
                        <Button color="primary" type="submit" disabled={editLoading}>
                            {editLoading ? <Spinner size="sm" /> : 'Save'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </AdminLayout>;
}